import { useSessionStorage } from '@cheqroom/hooks';
import { TFunction } from 'i18next';
import jwtDecode from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useRecentWorkspaces } from '../../api/find-workspaces.api';
import { useDoGetTokensQuery } from '../../api/get-tokens.api';
import { useDoFetchSsoTokens } from '../../api/get-tokens-for-sso-login.api';
import { getHasUserAcceptedTerms } from '../../api/terms-and-conditions.api';
import { Step } from '../../App';
import { redirectToApplication } from '../PasswordStep/api.hooks';

const APP_URL = process.env.APP_URL as string;

const getErrorMessage = (error: Error, t: TFunction) => {
	if (error.message.includes('403')) {
		return t('sso_redirect_step.no_access');
	}

	if (error.message.includes('402')) {
		return t('sso_redirect_step.subscription_expired');
	}

	return t('sso_redirect_step.sso_failed');
};

export const useLoginWithOAuthCode = () => {
	const [params] = useSearchParams();
	const navigateTo = useNavigate();
	const [isMobileApp] = useSessionStorage<boolean>('mobile');
	const [redirect_uri, _, clearStorage] = useSessionStorage<string>('CHEQROOM_REDIRECT_KEY');
	const { add: addRecentWorkspace } = useRecentWorkspaces();
	const { t } = useTranslation();

	// Get the params from the url and use them to identify with the python API
	const code = params.get('code');
	const state = params.get('state');

	return useDoFetchSsoTokens(
		{ code: code!, state: state!, redirectUri: `${window.location.origin}${Step.SSO_REDIRECT}` },
		{
			enabled: !!code && !!state,
			onSuccess: async ({ accessToken, refreshToken }) => {
				const decodedToken = jwtDecode<{ workspace: string; sub: string }>(accessToken);

				addRecentWorkspace(decodedToken.workspace);

				if (isMobileApp) {
					window.location.replace(`/mobile.html?access_token=${accessToken}&session_token=${refreshToken}`);
				} else {
					const hasUserAcceptedTerms = await getHasUserAcceptedTerms(decodedToken.sub, accessToken);

					if (!hasUserAcceptedTerms) {
						navigateTo(Step.ACCEPT_TERMS_OF_USE, {
							state: {
								accessToken,
								refreshToken,
								userId: decodedToken.sub,
								workspaceId: decodedToken.workspace,
								redirectUri: redirect_uri,
							},
						});
						return;
					}

					redirectToApplication({
						accessToken,
						refreshToken,
						workspaceId: decodedToken.workspace,
						clearStorage,
					});
				}
			},
			onError: (error) => {
				return navigateTo(Step.SELECT_WORKSPACE, {
					state: {
						error: getErrorMessage(error, t),
					},
				});
			},
		}
	);
};

export const useNewLoginWithOauthCode = () => {
	const [params] = useSearchParams();
	const navigateTo = useNavigate();
	const [isMobileApp] = useSessionStorage<boolean>('mobile');
	const [redirect_uri, _, clearStorage] = useSessionStorage<string>('CHEQROOM_REDIRECT_KEY');

	const { t } = useTranslation();

	// Get the params from the url and use them to identify with the python API
	const code = params.get('code');
	const state = params.get('state');

	return useDoGetTokensQuery(
		{
			code: code!,
			state: state!,
			redirect_uri: `${window.location.origin}${Step.SSO_REDIRECT}`,
			grant_type: 'authorization_code',
		},
		{
			enabled: !!code && !!state,
			onSuccess: ({ access_token, refresh_token }) => {
				const decodedToken = jwtDecode<{ workspace: string }>(access_token);

				if (isMobileApp) {
					window.location.replace(`/mobile.html?access_token=${access_token}&session_token=${refresh_token}`);
				} else {
					const url = new URL(`${APP_URL}/${decodedToken.workspace}`);

					if (redirect_uri) {
						url.searchParams.append('redirect_uri', redirect_uri);
					}

					url.hash = new URLSearchParams({
						access_token,
						session_token: refresh_token,
					}).toString();

					clearStorage();
					window.location.replace(url);
				}
			},
			onError: (error) => {
				return navigateTo(Step.SELECT_WORKSPACE, {
					state: {
						error: error?.message.includes('403')
							? t('sso_redirect_step.no_access')
							: t('sso_redirect_step.sso_failed'),
					},
				});
			},
		}
	);
};
